"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { getApplicationsByUserRole } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Application } from "../types";
import BaseApplicationList from "./BaseApplicationList";

interface FeatureApplicationListProps {
  titleKey: string;
  descriptionKey: string;
}

/**
 * A generic application list component that handles fetching and displaying
 * applications based on the current user's role.
 */
export default function FeatureApplicationList({
  titleKey,
  descriptionKey
}: FeatureApplicationListProps) {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile } = useAuth();
  const { t } = useTranslation(["applications"]);

  useEffect(() => {
    async function fetchApplications() {
      try {
        if (profile.anonymous) {
          throw new Error("No user found");
        }
        const data = await getApplicationsByUserRole(profile.id, profile.role);
        setApplications(data);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }

    if (!profile.anonymous) {
      fetchApplications();
    }
  }, [profile]);

  return (
    <div className="flex h-screen bg-white">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <div>
            <h1 className="text-2xl font-semibold">{t(titleKey)}</h1>
            <p className="text-gray-500">{t(descriptionKey)}</p>
          </div>
        </div>

        {/* Content */}
        <BaseApplicationList
          applications={applications}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
}
